/**
 * StatusScreen — Phase 1 home.
 *
 * Not a product screen: it is the visible proof that the spine works. It shows
 * the session, the active store, the device slot, sync state and realtime
 * connectivity, and lets a sync be triggered by hand. Phase 2 replaces it with
 * the billing screen; the sync indicator moves into the shell.
 */
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '@/store/useAppStore';
import { useSync } from '@/sync/useSync';
import { useAuth } from '@/auth/useAuth';
import { countUnsyncedRecords, getActiveStoreId, requestPersistentStorage } from '@/db';
import { useDeviceRegistrationStore } from '@/services/deviceRegistrationStore';

function timeAgo(ts: number | null): string {
  if (!ts) return 'never';
  const secs = Math.round((Date.now() - ts) / 1000);
  if (secs < 60) return `${secs}s ago`;
  if (secs < 3600) return `${Math.round(secs / 60)}m ago`;
  return `${Math.round(secs / 3600)}h ago`;
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-stroke-hairline p-3 last:border-0">
      <span className="text-label uppercase text-fg-tertiary">{label}</span>
      <span className="text-body text-right">{value}</span>
    </div>
  );
}

export function StatusScreen() {
  const { isSyncing, lastSyncAt, syncError, triggerSync } = useSync();
  const { logout } = useAuth();
  const { currentUser, activeStoreId, deviceId, deviceSlot } = useAppStore(
    useShallow((s) => ({
      currentUser: s.currentUser,
      activeStoreId: s.activeStoreId,
      deviceId: s.deviceId,
      deviceSlot: s.deviceSlot,
    })),
  );

  const deviceRegistered = useDeviceRegistrationStore((s) => s.registered);
  const deviceWarning = useDeviceRegistrationStore((s) => s.warning);

  const [unsynced, setUnsynced] = useState<number | null>(null);
  const [persisted, setPersisted] = useState<boolean | null>(null);

  // Recount after every sync settles, so the number reflects reality rather
  // than whatever it was at mount.
  useEffect(() => {
    let alive = true;
    if (isSyncing) return;
    void countUnsyncedRecords()
      .then((n) => alive && setUnsynced(n))
      .catch(() => alive && setUnsynced(null));
    return () => {
      alive = false;
    };
  }, [isSyncing, lastSyncAt]);

  useEffect(() => {
    void requestPersistentStorage().then(setPersisted);
  }, []);

  return (
    <main className="mx-auto max-w-xl p-6">
      <header className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-heading">BigxBook Web</h1>
          <p className="text-body text-fg-secondary">
            Phase 1 spine — login, store, device, sync, realtime.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void logout()}
          className="rounded-md border border-stroke-hairline px-3 py-2 text-body"
        >
          Log out
        </button>
      </header>

      <section className="mb-4 rounded-md border border-stroke-hairline bg-bg-surface">
        <Row label="User" value={currentUser?.name || currentUser?.phone || '—'} />
        <Row label="Role" value={currentUser?.role ?? '—'} />
        <Row label="Merchant" value={currentUser?.merchantId ?? '—'} />
        <Row label="Active store" value={activeStoreId ?? '—'} />
        <Row label="Database" value={getActiveStoreId() ? `bigxbook-web-${getActiveStoreId()}` : 'not activated'} />
        <Row label="Device" value={deviceId ?? 'unregistered'} />
        {/* Slot prefixes locally generated invoice numbers, so a missing slot
            means offline invoice numbering is not yet collision-proof. */}
        <Row label="Device slot" value={deviceSlot === null ? 'not assigned' : String(deviceSlot)} />
      </section>

      <section className="mb-4 rounded-md border border-stroke-hairline bg-bg-surface">
        <Row label="Sync" value={isSyncing ? 'syncing…' : `last ${timeAgo(lastSyncAt)}`} />
        <Row label="Unsynced records" value={unsynced === null ? '—' : String(unsynced)} />
        <Row
          label="Storage"
          value={persisted === null ? '—' : persisted ? 'persistent' : 'evictable — unsafe for offline billing'}
        />
      </section>

      {/* A refused registration is the usual cause of a till that bills fine
          but never syncs: /auth/register-device answers 201 with
          registered:false when the plan device limit is reached, and the
          backend then 503s every sync pull because it cannot allocate a slot. */}
      {deviceRegistered === false && (
        <div
          role="alert"
          className="mb-4 rounded-md border border-warning/40 bg-warning/10 p-3 text-body"
        >
          <strong>This device is not registered.</strong>{' '}
          {deviceWarning ?? 'The server refused registration.'} Billing still works and
          sales are saved locally, but this device cannot sync until it is registered.
        </div>
      )}

      {syncError && (
        <div role="alert" className="mb-4 rounded-md border border-danger/40 bg-danger/10 p-3 text-body text-danger-ink">
          {syncError}
        </div>
      )}

      {/* AppShell has its own header sync control with the same label, so this
          one carries a test id to keep e2e selectors unambiguous. */}
      <button
        type="button"
        data-testid="status-sync-now"
        onClick={triggerSync}
        disabled={isSyncing}
        className="btn-brand w-full rounded-md border border-white/10 p-3 text-body font-medium disabled:opacity-40"
      >
        {isSyncing ? 'Syncing…' : 'Sync now'}
      </button>
    </main>
  );
}
